import { ReactNode, createContext, Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import {
  City,
  GradeLevel,
  BookingLanguage,
  PreferredTime,
  Subject,
  Subjects,
  Trait,
  EducationalDegree,
} from '~/types/booking'
import { Tutor, Preference, Student } from '~/types/tutoring-api'
import { UserRole } from '~/types/user-info-types'
import { countryData as countries } from '~/shared/constants/countryData.const'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'
import {
  getBrowserTimezone,
  getCountryCallingCodeFromAlpha2Code,
  getCountryCodesFromPhoneNumber,
  isValidPhoneNumber,
} from '~/utils/helpers'
import todAnalytics from '~/analytics/tod/todAnalytics'

export interface UserSettingsFields {
  city: City | ''
  countryCallingCode: string
  countryCode: string
  description?: string
  educationalDegree?: EducationalDegree
  fullName: string
  gradeLevels?: GradeLevel[]
  institute?: string
  languagePrimary?: BookingLanguage
  languageSecondary?: BookingLanguage
  languages?: BookingLanguage[]
  phone: string
  schoolCountry?: (typeof countries)[number] | ''
  subjects: Subjects
  timeslots: PreferredTime[]
  timezone: string
  traits: Trait[]
}

export type UserSettingsErrors = {
  [T in keyof UserSettingsFields as string]?: string
}

function constructSubjectFields(preference?: Preference): Subjects {
  const { schoolSystem, levelsOfEducation, subjects, gradeLevels } = preference || {}

  return {
    schoolSystem,
    levelsOfEducation: Array.isArray(levelsOfEducation) ? levelsOfEducation : [],
    subjects:
      subjects?.map((subject: Subject) =>
        subject === Subject.MATH ? { name: subject, gradeLevels: gradeLevels ?? [] } : { name: subject },
      ) || [],
  }
}

const getDefaultUserFields = (user: Student | Tutor): UserSettingsFields => {
  const commonFields: Omit<UserSettingsFields, 'description' | 'institute' | 'educationalDegree'> = {
    city: user.preference?.city || '',
    countryCallingCode: user.countryCallingCode || (!user.countryCode ? '+47' : ''),
    countryCode: user.countryCode || (!user.countryCallingCode ? 'NO' : ''),
    fullName: user.fullName || '',
    phone: user.phone || '',
    timeslots: user.preference?.timeslots || [],
    subjects: constructSubjectFields(user.preference),
    timezone: user?.timezone || getBrowserTimezone(),
    traits: user.preference?.traits || [],
  }

  if ([UserRole.TUTOR, UserRole.ADMIN].includes(user.role)) {
    return {
      ...commonFields,
      institute: 'institute' in user ? user.institute || '' : '',
      description: 'description' in user ? user.description || '' : '',
      educationalDegree: 'educationalDegree' in user ? user.educationalDegree || undefined : undefined,
      languages: user.preference && 'languages' in user.preference ? user.preference.languages || [] : [],
    }
  } else {
    return {
      ...commonFields,
      gradeLevels: user.preference?.gradeLevels ?? [],
      languagePrimary: user.preference?.languagePrimary,
      languageSecondary: user.preference?.languageSecondary,
      schoolCountry: user.preference?.schoolCountry || '',
    }
  }
}

export interface Context {
  errors: UserSettingsErrors
  fields: UserSettingsFields
  onSubmit: (fields: UserSettingsFields) => void
  setErrors: Dispatch<SetStateAction<UserSettingsErrors>>
  setFields: Dispatch<SetStateAction<UserSettingsFields>>
  logFieldChange: (fieldName: string) => void
}

export const SettingsFormContext = createContext<Context | null>(null)

export default function SettingsFormProvider({
  children,
  user,
  onSubmit,
  formType = 'unknown',
}: {
  children: ReactNode
  user: Tutor | Student
  onSubmit: (fields: UserSettingsFields) => void
  formType: string
}): React.ReactElement {
  const [fields, setFields] = useState(getDefaultUserFields(user))
  const [errors, setErrors] = useState<UserSettingsErrors>({})
  const { data } = useGetUserLocationInfoFromIpQuery()

  useEffect(() => {
    setFields(getDefaultUserFields(user))
  }, [user])

  useEffect(() => {
    const phone = fields.phone

    if (phone && isValidPhoneNumber(phone)) {
      const [countryCallingCode, countryCode] = getCountryCodesFromPhoneNumber(phone)

      setFields((prevState) => ({
        ...prevState,
        countryCallingCode,
        countryCode,
        phone: phone.split(':')[1],
      }))
    }

    if (!phone) {
      const countryCallingCode = getCountryCallingCodeFromAlpha2Code(data?.countryCode)

      setFields((prevState) => ({
        ...prevState,
        countryCallingCode: countryCallingCode ?? '+47',
        countryCode: data?.countryCode ?? 'NO',
      }))
    }
  }, [data?.countryCode, fields.phone])

  const logFieldChange = useDebouncedCallback(
    (fieldName: string) => {
      todAnalytics.changeInSelectOption(fieldName, undefined, formType)
    },
    500,
    { leading: true, trailing: false },
  )

  const context = {
    errors,
    fields,
    onSubmit,
    setErrors,
    setFields,
    logFieldChange,
  }

  return <SettingsFormContext.Provider value={context}>{children}</SettingsFormContext.Provider>
}
